import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tabs,
  Typography,
  Tag,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  SaveOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../../PagesWrapper';
import ImageDropZone from '../../../components/ImageDropZone/ImageDropZone';
import RichTextEditor from '../../../components/RichTextEditor';
import { AccessBanner, OwnershipManager } from '../../../components/Ownership';
import useOwnership from '../../../hooks/useOwnership';
import * as newsService from '../../../services/newsService';
import { getAllCountries } from '../../../services/countriesService';
import { getFileUrl } from '../../../utils/urlUtils';
import { useAuth } from '../../../context/AuthContext';
import { AppRoles } from '../../../services/authService';
import styles from './NewsEditor.module.css';

const { Title, Text } = Typography;

const emptyInstance = () => ({ countryId: null, title: '', body: '' });

/**
 * NewsEditor provides multilingual News authoring with one rich-text body per country/language instance.
 */
const NewsEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const newsId = isNew ? null : parseInt(id, 10);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(AppRoles.Admin);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [news, setNews] = useState(null);
  const [countries, setCountries] = useState([]);
  const [instances, setInstances] = useState([emptyInstance()]);
  const [coverImage, setCoverImage] = useState(null);
  const [clearCoverImage, setClearCoverImage] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [stats, setStats] = useState(null);

  const ownership = useOwnership('News', newsId, { autoLoad: !!newsId });

  const canEdit = isNew || ownership.canEdit;
  const isPublished = Form.useWatch('isPublished', form) ?? news?.isPublished ?? false;

  const fetchCountries = useCallback(async () => {
    try {
      const result = await getAllCountries({ suppressNotification: true });
      setCountries(result || []);
    } catch (error) {
      message.error(t('news.countriesFetchError', 'Failed to load countries'));
    }
  }, [t]);

  const fetchNews = useCallback(async ({ showPageLoader = false } = {}) => {
    if (isNew) return;
    if (showPageLoader) {
      setLoading(true);
    }

    try {
      const result = await newsService.getNewsDetail(newsId);
      setNews(result);
      form.setFieldsValue({
        name: result.name,
        slug: result.slug,
        isPublished: result.isPublished,
      });
      setInstances((result.instances || []).map(instance => ({
        countryId: instance.countryId,
        title: instance.title,
        body: instance.body,
      })));
    } catch (error) {
      message.error(t('news.fetchDetailError', 'Failed to load News'));
      navigate('/admin/news');
    } finally {
      if (showPageLoader) {
        setLoading(false);
      }
    }
  }, [form, isNew, navigate, newsId, t]);

  const fetchStats = useCallback(async () => {
    if (isNew) return;
    try {
      const result = await newsService.getNewsStatistics(newsId, 30, { suppressNotification: true });
      setStats(result);
    } catch (error) {
      setStats(null);
    }
  }, [isNew, newsId]);

  useEffect(() => {
    fetchCountries();
    fetchNews({ showPageLoader: true });
    fetchStats();
    // Initial editor load only; callbacks are intentionally invoked once for the route id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateInstance = (index, patch) => {
    setInstances(prev => prev.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  };

  const addInstance = () => setInstances(prev => [...prev, emptyInstance()]);

  const removeInstance = (index) => {
    setInstances(prev => prev.length <= 1 ? prev : prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const validateInstances = () => {
    const usedCountries = new Set();
    for (const instance of instances) {
      const plainText = (instance.body || '').replace(/<[^>]*>/g, '').trim();
      if (!instance.countryId || !instance.title?.trim() || !plainText) {
        message.error(t('news.instanceValidation', 'Each News instance needs a country, title, and body.'));
        return false;
      }
      if (usedCountries.has(instance.countryId)) {
        message.error(t('news.duplicateCountry', 'Each country can only be used once.'));
        return false;
      }
      usedCountries.add(instance.countryId);
    }
    return true;
  };

  const handleNameBlur = () => {
    const currentSlug = form.getFieldValue('slug');
    const name = form.getFieldValue('name');
    if (!currentSlug && name) {
      form.setFieldValue('slug', newsService.createNewsSlug(name));
    }
  };

  const handleSubmit = async () => {
    if (!validateInstances()) return;
    setSaving(true);
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        clearCoverImage,
        instances: instances.map(instance => ({
          countryId: instance.countryId,
          title: instance.title.trim(),
          body: instance.body,
        })),
      };

      if (isNew) {
        const created = await newsService.createNews(payload, coverImage);
        message.success(t('news.createSuccess', 'News created successfully'));
        navigate(`/admin/news/${created.id}`);
      } else {
        await newsService.updateNews(newsId, payload, coverImage);
        message.success(t('news.updateSuccess', 'News updated successfully'));
        setCoverImage(null);
        setClearCoverImage(false);
        await Promise.all([
          fetchNews(),
          fetchStats(),
        ]);
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || t('news.saveError', 'Failed to save News'));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    if (isNew || !newsId) return;

    try {
      setStatusUpdating(true);
      if (news?.isPublished) {
        await newsService.unpublishNews(newsId);
        message.success(t('news.unpublishSuccess', 'News unpublished'));
      } else {
        await newsService.publishNews(newsId);
        message.success(t('news.publishSuccess', 'News published'));
      }
      await Promise.all([
        fetchNews(),
        fetchStats(),
      ]);
    } catch (error) {
      message.error(error.message || t('news.publishError', 'Failed to update News status'));
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (isNew || !newsId) return;

    try {
      await newsService.deleteNews(newsId);
      message.success(t('news.deleteSuccess', 'News deleted'));
      navigate('/admin/news');
    } catch (error) {
      message.error(error.message || t('news.deleteError', 'Failed to delete News'));
    }
  };

  const handleCopyPublicLink = async () => {
    if (!news) return;

    await navigator.clipboard.writeText(`${window.location.origin}${newsService.getNewsPublicPath(news)}`);
    message.success(t('news.linkCopied', 'News link copied'));
  };

  const renderInstanceEditor = (instance, index) => {
    const selectedCountry = countries.find(country => country.id === instance.countryId);
    const options = countries.map(country => ({
      value: country.id,
      label: country.language,
      disabled: country.id !== instance.countryId && instances.some(item => item.countryId === country.id),
    }));

    return (
      <Card
        key={index}
        className={styles.instanceCard}
        title={selectedCountry ? selectedCountry.language : t('news.newInstance', 'News translation')}
        extra={instances.length > 1 && canEdit ? (
          <Popconfirm title={t('news.removeInstanceConfirm', 'Remove this News instance?')} onConfirm={() => removeInstance(index)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ) : null}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Text strong className={styles.fieldLabel}>{t('news.countryLanguage', 'Language')}</Text>
            <Select
              showSearch
              value={instance.countryId}
              options={options}
              optionFilterProp="label"
              placeholder={t('news.selectLanguage', 'Select language')}
              disabled={!canEdit}
              onChange={(value) => updateInstance(index, { countryId: value })}
              className={styles.fullWidth}
            />
          </Col>
          <Col xs={24} md={16}>
            <Text strong className={styles.fieldLabel}>{t('news.title', 'Title')}</Text>
            <Input
              value={instance.title}
              placeholder={t('news.titlePlaceholder', 'Localized News title')}
              disabled={!canEdit}
              maxLength={255}
              onChange={(event) => updateInstance(index, { title: event.target.value })}
            />
          </Col>
          <Col span={24}>
            <Text strong className={styles.fieldLabel}>{t('news.body', 'Body')}</Text>
            <RichTextEditor
              value={instance.body}
              onChange={(html) => updateInstance(index, { body: html })}
              maxLength={20000}
              placeholder={t('news.bodyPlaceholder', 'Write the full News article in this language...')}
              disabled={!canEdit}
              parentType="News"
              parentId={newsId}
            />
          </Col>
        </Row>
      </Card>
    );
  };

  if (loading) {
    return <PagesWrapper><div className={styles.loading}><Spin size="large" /></div></PagesWrapper>;
  }

  return (
    <PagesWrapper>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/news')}>{t('common.back', 'Back')}</Button>
            <Title level={2} className={styles.pageTitle}>{news?.name || (isNew ? t('news.createNews', 'Create News') : t('news.editNews', 'Edit News'))}</Title>
            {!isNew && (
              <>
                <Tag color={isPublished ? 'green' : 'default'}>
                  {isPublished ? t('news.published', 'Published') : t('news.draft', 'Draft')}
                </Tag>
                {news?.addedByName && (
                  <Tooltip title={t('quiz.createdBy', 'Created by')}>
                    <Space size="small" className={styles.createdBy}>
                        <Avatar 
                            size="small" 
                            src={getFileUrl(news.addedByProfileImageUrl)}
                            icon={!news.addedByProfileImageUrl && <UserOutlined />}
                        />
                        <Text type="secondary">{news.addedByName}</Text>
                    </Space>
                </Tooltip>
                )}
                
              </>
            )}
          </div>
          <Space className={styles.headerActions}>
            {!isNew && news?.isPublished && (
              <>
                <Button icon={<EyeOutlined />} onClick={() => window.open(newsService.getNewsPublicPath(news), '_blank')}>
                  {t('news.openPublic', 'Open public page')}
                </Button>
                <Button icon={<CopyOutlined />} onClick={handleCopyPublicLink}>
                  {t('news.copyLink', 'Copy link')}
                </Button>
              </>
            )}
            {!isNew && ownership.canManageOwnership && (
              <Button icon={<TeamOutlined />} onClick={() => setOwnershipOpen(true)}>{t('ownership.share', 'Share')}</Button>
            )}
            {!isNew && (
              <Button icon={news?.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />} onClick={handleTogglePublish} loading={statusUpdating}>
                {news?.isPublished ? t('news.unpublish', 'Unpublish') : t('news.publish', 'Publish')}
              </Button>
            )}
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit} disabled={!canEdit || (isNew && isAdmin)}>
              {t('common.save', 'Save')}
            </Button>
            {!isNew && (
              <Popconfirm title={t('news.deleteConfirm', 'Delete this News item?')} onConfirm={handleDelete} okText={t('common.delete', 'Delete')}>
                <Button danger icon={<DeleteOutlined />}>
                  {t('common.delete', 'Delete')}
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        {isNew && isAdmin && (
          <Alert type="info" showIcon className={styles.alert} message={t('news.adminCreateBlocked', 'Admins assign and supervise News creators. Please assign a content-creator to create owned News.')} />
        )}
        {!isNew && ownership.ownershipInfo && (
          <AccessBanner
            accessLevel={ownership.ownershipInfo.accessLevel}
            ownerInfo={ownership.ownershipInfo.mainOwner}
            canEdit={ownership.ownershipInfo.canEdit}
            canDelete={ownership.ownershipInfo.canDelete}
          />
        )}

        <Tabs
          items={[
            {
              key: 'content',
              label: t('news.contentTab', 'Content'),
              children: (
                <Space direction="vertical" size={18} className={styles.fullWidth}>
                  <Card title={t('news.details', 'Details')} className={styles.editorCard}>
                    <Form form={form} layout="vertical" disabled={!canEdit || (isNew && isAdmin)}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                          <Form.Item name="name" label={t('news.name', 'Name')} rules={[{ required: true, message: t('news.nameRequired', 'Name is required') }]}>
                            <Input maxLength={255} onBlur={handleNameBlur} placeholder={t('news.namePlaceholder', 'Internal name for this multilingual News item')} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="slug" label={t('news.slug', 'Slug')}>
                            <Input maxLength={255} placeholder={t('news.slugPlaceholder', 'seo-friendly-url')} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label={t('news.coverImage', 'Cover image')}>
                            <ImageDropZone
                              value={coverImage}
                              onChange={setCoverImage}
                              existingUrl={!clearCoverImage ? getFileUrl(news?.coverImageUrl) : null}
                              onClearExisting={() => setClearCoverImage(true)}
                              disabled={!canEdit || (isNew && isAdmin)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  </Card>

                  <div className={styles.instancesHeader}>
                    <div className={styles.sectionHeading}>
                      <Title level={4}>{t('news.languageInstances', 'Language instances')}</Title>
                      <Text type="secondary">{t('news.instancesHelp', 'Add at least one language-specific title and rich text body. Public pages use the current UI language to choose the matching article.')}</Text>
                    </div>
                    {canEdit && !(isNew && isAdmin) && <Button icon={<PlusOutlined />} onClick={addInstance}>{t('news.addInstance', 'Add instance')}</Button>}
                  </div>
                  {instances.map(renderInstanceEditor)}
                </Space>
              ),
            },
            ...(!isNew ? [{
              key: 'stats',
              label: t('news.statistics', 'Statistics'),
              children: (
                <Card title={t('news.statistics', 'Statistics')} className={styles.editorCard}>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}><Statistic title={t('news.totalVisits', 'Total visits')} value={stats?.totalVisits || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('news.uniqueVisitors', 'Unique visitors')} value={stats?.uniqueVisitors || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('news.languages', 'Languages')} value={stats?.byLanguage?.length || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('news.instances', 'Instances')} value={instances.length} /></Col>
                  </Row>
                  <div className={styles.breakdown}>
                    {(stats?.byLanguage || []).map(item => (
                      <div key={`${item.languageCode}-${item.countryId}`} className={styles.breakdownRow}>
                        <span>{item.countryName || item.countryLanguage || item.languageCode || t('common.unknown', 'Unknown')}</span>
                        <strong>{item.visits}</strong>
                      </div>
                    ))}
                  </div>
                </Card>
              ),
            }] : []),
          ]}
        />
      </div>
      {!isNew && (
        <OwnershipManager
          open={ownershipOpen}
          onClose={() => setOwnershipOpen(false)}
          ownershipInfo={ownership.ownershipInfo}
          onShare={ownership.shareWith}
          onRevoke={ownership.revoke}
          onGetAvailableUsers={ownership.getAvailableUsers}
          availableUsers={ownership.availableUsers}
          loading={ownership.loading}
          loadingAvailableUsers={ownership.loadingAvailableUsers}
          canManageOwnership={ownership.canManageOwnership}
        />
      )}
    </PagesWrapper>
  );
};

export default NewsEditor;
